import { useCallback, useMemo } from 'react'
import {
  PRRect,
  usePRFile,
  usePan,
  useZoom,
} from '@sketch-hq/sketch-web-renderer'

/**
 * Take as input a PRRect (with units relative to the scene) and return a corresponding rect
 * relative to the canvas HTML element based on the current zoom and pan values.
 * This is useful for positioning overlays on top of the canvas.
 */
export function useMapPRRectToCanvasRelativeRect(bounds: PRRect) {
  const mapPointToCanvasRelativePosition = useGetMapPointToCanvasRelativePosition()
  const mapSizeToCanvasRelativeSize = useGetMapSizeToCanvasRelativeSize()

  return useMemo(() => {
    const width = mapSizeToCanvasRelativeSize(bounds.width)
    const height = mapSizeToCanvasRelativeSize(bounds.height)
    const position = mapPointToCanvasRelativePosition(bounds)

    if (position === null || height === null || width === null) {
      return null
    }

    return { ...position, width, height }
  }, [bounds, mapPointToCanvasRelativePosition, mapSizeToCanvasRelativeSize])
}

function useRootNodeExtent() {
  const prFile = usePRFile()

  return useMemo<PRRect | null>(
    () => prFile?.getRootNode()?.getAbsoluteExtent() ?? null,
    [prFile]
  )
}

/**
 * Returns a function that take as input some coordinates (with units relative to the scene)
 * and return corresponding coordinates relative to the canvas HTML element based on the current zoom and pan values.
 * This is useful for positioning overlays on top of the canvas.
 */
export function useGetMapPointToCanvasRelativePosition() {
  const zoom = useZoom() ?? 1
  const pan = usePan() ?? { x: 0, y: 0 }

  const rootNodeExtent = useRootNodeExtent()

  return useCallback(
    (point: { x: number; y: number }) => {
      if (!rootNodeExtent) {
        return null
      }

      const x = (pan.x + point.x - rootNodeExtent.x) * zoom
      const y = (pan.y + point.y - rootNodeExtent.y) * zoom

      return { x, y }
    },
    [pan.x, pan.y, rootNodeExtent, zoom]
  )
}

/**
 * Returns a function that take as input a size/distance (with unit relative to the scene)
 * and return a corresponding size relative to the canvas HTML element based on the current zoom and pan values.
 * This is useful for positioning overlays on top of the canvas.
 */
export function useGetMapSizeToCanvasRelativeSize() {
  const zoom = useZoom() ?? 1

  const rootNodeExtent = useRootNodeExtent()

  return useCallback(
    (size: number) => {
      if (!rootNodeExtent) {
        return null
      }

      return size * zoom
    },
    [rootNodeExtent, zoom]
  )
}
